import {
  AccountReceivableModel,
  AccountReceivableMutationInput,
} from 'api-hooks/account-receivable';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import { ReceiptModel } from 'api-hooks/receipt';
import { TransactionalStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState } from 'components/elements';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
  Title,
  TitleContainer,
} from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { AccountReceivableTableForm } from 'legacy-modules/transactions/component/table-form-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { accountReceivableSchema, FormType } from './form-type';
import InformationField from './information-field';
import PaymentField from './payment-field';
import SummaryCard from './summary-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  accountReceivable?: AccountReceivableModel;
  receiptData?: ReceiptModel;
  onSubmit: (
    input: AccountReceivableMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<AccountReceivableModel | undefined>;
}

export default function AccountReceivableForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { accountReceivable, receiptData } = props;

  const defaultValues = useGetDefaultValues({
    accountReceivable,
    receiptData,
  });

  const resolver = useYupValidationResolver(accountReceivableSchema());

  const methods = useForm({
    resolver,
    defaultValues,
    mode: 'all',
  });

  const agentId = methods.getValues('customerId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      methods.reset();
    }
  }, [agentId, methods]);

  const onSubmit = React.useCallback(
    async (values: FormType) => {
      try {
        const paymentItems = values.paymentItems.filter((item) => item.isCheck);
        values.needApproval =
          values.transactionAt.getTime() !== values.tempTransactionAt.getTime();
        if (paymentItems.length <= 0) {
          methods.setError('paymentItems', {
            message: t('error:at_least_select_one_item'),
          });
          return;
        }
        await props.onSubmit(
          {
            ...values,
            paymentItems,
            paymentableId:
              values.paymentMethod === PaymentMethodEnum.Balance
                ? values.customerId
                : values.paymentableId,
          } as AccountReceivableMutationInput,
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props, t],
  );

  const isActive = accountReceivable?.status === TransactionalStatusEnum.Active;

  const permission = useGetCrudPermission('account-receivables');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'account-receivables',
    accountReceivable,
  );
  const { requestApprovalButton } = useApprovalAction(
    'account-receivables',
    accountReceivable,
  );
  const { resolverButton } = useResolveApproval(
    'account-receivables',
    accountReceivable,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'account-receivables',
    accountReceivable,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (accountReceivable) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    accountReceivable,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!accountReceivable}
      customViewName={
        accountReceivable &&
        `${t('common:account_receivable')} - ${accountReceivable.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={accountReceivable}
            title={
              accountReceivable ? (
                <TitleContainer>
                  <Title variant="HeadingXSmall">{`${t(
                    'common:account_receivable',
                  )} - ${accountReceivable.number}`}</Title>
                  <TransactionPillStatus value={accountReceivable.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', {
                  extra: t('common:account_receivable'),
                })
              )
            }
            onDelete={onVoid}
            deleteText={t('common:void')}
            isDeleteLoading={voidMutate.isLoading}
            noEdit={!isActive || !permission.update}
            noDelete={editable || !voidPermission}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <InformationField
          isFilled={!!accountReceivable}
          hasReceipt={!!receiptData}
        />
        {methods?.formState?.errors?.paymentItems && (
          <>
            <Text variant="LabelMedium" color="red">
              {methods?.formState?.errors?.paymentItems.message}
            </Text>
            <Separator gap={8} />
          </>
        )}
        <AccountReceivableTableForm
          isFilled={!!accountReceivable}
          receiptData={receiptData}
        />
        <SeparatorVertical $margin={48} />
        <SectionContainer>
          <HalfContainer>
            <SummaryCard />
          </HalfContainer>
          <SeparatorVertical $margin={24} />
          <HalfContainer>
            <PaymentField accountReceivable={accountReceivable} />
          </HalfContainer>
        </SectionContainer>
        <Separator gap={16} />
      </div>
    </Form>
  );
}
